import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Filter, Truck, Loader, X, Package, Clock, Users, LayoutGrid, Calendar, User, CreditCard, Megaphone, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Checkbox, message, Modal } from 'antd';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { ordersApi, employeesApi, productsApi, orderCampaignApi, fetchApiDirect } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import { usePermissions } from '../hooks/Usepermissions';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import { formatDate } from '../utils/helpers';
import DateRangeFilter from '../components/DateRangeFilter';
import { BASE_URL as API_URL, ASSET_BASE_URL as ASSET_URL } from '../services/api';

const COURIER_APIS = {
    delhivery:       { name: 'Delhivery',       logo: '📮' },
    shiprocket:      { name: 'Shiprocket',      logo: '🚀' },
    fship:           { name: 'FShip',           logo: '🚢' },
    shadowfax:       { name: 'Shadowfax',       logo: '⚡' },
    ekart:           { name: 'Ekart',           logo: '🟠' },
    parcelx:         { name: 'ParcelX',         logo: '📦' },
    not_serviceable: { name: 'Not Serviceable', logo: '🚫' },
};

const SR_STEPS = { CREATE: 'create', PICK: 'pick', DISPATCHING: 'dispatching', DONE: 'done' };

// ── Shared helper: part payment ke liye sahi payment type aur COD amount nikalo ──
const resolvePaymentInfo = (order) => {
  const isPartPay = order.paymentType === 'PART_PAYMENT';
  const isPrepaid = ['ONLINE', 'PREPAID', 'UPI', 'CARD'].includes(
    String(order.paymentMode || '').toUpperCase()
  );
  // Agar fully prepaid (aur part payment nahi) → PREPAID, warna COD
  const isCodMode = !isPrepaid || isPartPay;

  const pendingAmt = Math.max(
    0,
    parseFloat(order.pendingAmount) ||
    parseFloat(order.restAmount)    ||
    ((parseFloat(order.grandTotal) || 0) - (parseFloat(order.prepaidAmount) || 0))
  );

  const codAmount = isCodMode
    ? (isPartPay ? pendingAmt : (parseFloat(order.grandTotal) || 0))
    : 0;

  return { isCodMode, isPartPay, isPrepaid, pendingAmt, codAmount };
};

function DispatchOrderverified() {
  const { currentTheme } = useTheme();
  const { has, isAdmin }  = usePermissions();
  const navigate           = useNavigate();
  const location           = useLocation();

  const [orders,          setOrders]          = useState([]);
  const [searchTerm,      setSearchTerm]       = useState('');
  const [orderLimit,      setOrderLimit]       = useState(20);
  const [selectedOrders,  setSelectedOrders]   = useState([]);
  const [loading,         setLoading]          = useState(false);
  const [totalOrders,     setTotalOrders]      = useState(0);
  const [currentPage,     setCurrentPage]      = useState(1);
  const [selectedCourier, setSelectedCourier]  = useState('All courier');
  const [selectedEmployee,setSelectedEmployee] = useState('All employees');
  const isDark = currentTheme.textColor === '#FFFFFF';

  const [activePageTab, setActivePageTab] = useState('dispatch');

  const [isGlobalPhoneSearch, setIsGlobalPhoneSearch] = useState(false);
  const [globalPhoneNumber,   setGlobalPhoneNumber]   = useState('');

  // Bulk dispatch
  const [showBulkDispatchModal,  setShowBulkDispatchModal]  = useState(false);
  const [bulkDispatchCourier,    setBulkDispatchCourier]    = useState('delhivery');
  const [bulkDispatchAwbs,       setBulkDispatchAwbs]       = useState({});
  const [bulkDispatchDate,       setBulkDispatchDate]       = useState(null);
  const [bulkDispatchRemarks,    setBulkDispatchRemarks]    = useState('');
  const [isDispatchingBulk,      setIsDispatchingBulk]      = useState(false);
  const [dispatchProgress,       setDispatchProgress]       = useState({ completed: 0, total: 0 });
  const [showDispatchAlert,      setShowDispatchAlert]      = useState(false);
  const [bulkDispatchResults,    setBulkDispatchResults]    = useState(null);
  const [isDownloading,          setIsDownloading]          = useState(false);

  // NS state
  const nsReasonRef      = useRef('');
  const nsPinCodeRef     = useRef('');
  const nsCourierNameRef = useRef('');
  const [isMarkingNS,   setIsMarkingNS]   = useState(false);
  const [nsHasReason,   setNsHasReason]   = useState(false);
  const [nsOrders,         setNsOrders]         = useState([]);
  const [nsLoading,        setNsLoading]        = useState(false);
  const [nsTotalItems,     setNsTotalItems]     = useState(0);
  const [nsCurrentPage,    setNsCurrentPage]    = useState(1);
  const [requeueingId,     setRequeuingId]      = useState(null);

  // Shiprocket
  const [srStep,                setSrStep]                = useState(SR_STEPS.CREATE);
  const [srCreatedOrders,       setSrCreatedOrders]       = useState([]);
  const [srCourierOptions,      setSrCourierOptions]      = useState([]);
  const [srLoadingCouriers,     setSrLoadingCouriers]     = useState(false);
  const [srSelectedCourierId,   setSrSelectedCourierId]   = useState(null);
  const [srSelectedCourierName, setSrSelectedCourierName] = useState('');

  // Filter state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const emptyFilters = {
    datePreset:   '',
    startDate:    '',
    endDate:      '',
    employeeId:   '',
    employeeName: '',
    teamId:       '',
    teamLeadName: '',
    category:     '',
    paymentMode:  '',
    campaignName: '',
    actionBy:     '',
  };

  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [tempFilters,   setTempFilters]   = useState(emptyFilters);

  const activeFiltersRef = useRef(emptyFilters);
  const searchRef        = useRef('');

  useEffect(() => { activeFiltersRef.current = activeFilters; }, [activeFilters]);

  const [filterOptions, setFilterOptions] = useState({
    employees:  [],
    teamLeads:  [],
    categories: [],
    campaigns:  [],
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);

  useEffect(() => {
    fetchVerifiedOrders({ page: 0 });
    fetchFilterOptions();
    fetchNsOrders(0);
  }, []);

  // Mount effect already fetchVerifiedOrders({page:0}) call karta hai; ye effect
  // bhi mount par chal jaata tha → duplicate call. Pehla render skip karo.
  const skipFirstSearch = useRef(true);
  useEffect(() => {
    searchRef.current = searchTerm;
    if (skipFirstSearch.current) {
      skipFirstSearch.current = false;
      return;
    }
    const t = setTimeout(() => {
      setCurrentPage(1);
      fetchVerifiedOrders({ page: 0, overrideSearch: searchTerm });
    }, 600);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const triggerDialer = (order) => {
    const lead = {
      id:            order.leadId || order.id,
      customerName:  order.customerName || order.lead?.customerName || '',
      contactNumber: order.contactNumber || order.lead?.contactNumber || '',
      campaign:      order.campaignName  || order.campaign           || '',
    };
    window.dispatchEvent(new CustomEvent('crm:dial', { detail: { lead } }));
  };

  useEffect(() => {
    let id;
    if (totalOrders > 0) {
      setShowDispatchAlert(true);
      id = setInterval(() => setShowDispatchAlert(true), 600000);
    }
    return () => { if (id) clearInterval(id); };
  }, [totalOrders]);

  useEffect(() => {
    if (showDispatchAlert) {
      const t = setTimeout(() => setShowDispatchAlert(false), 5000);
      return () => clearTimeout(t);
    }
  }, [showDispatchAlert]);

  useEffect(() => {
    if (isFilterModalOpen) setTempFilters(activeFilters);
  }, [isFilterModalOpen]);

  const getCurrentUser = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  };

  const getProductName = (order) => {
    if (order.orderItems?.length > 0)
      return order.orderItems.length === 1
        ? order.orderItems[0].productName
        : `${order.orderItems[0].productName} +${order.orderItems.length - 1} more`;
    return order.productName || '-';
  };

  const resolveDatePreset = (preset, customStart = '', customEnd = '') => {
    const now = new Date();
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fmtS = (d) => { d.setHours(0, 0, 0, 0);     return fmt(d); };
    const fmtE = (d) => { d.setHours(23, 59, 59, 999); return fmt(d); };

    switch (preset) {
      case 'today':
        return { startDate: fmtS(new Date(now)), endDate: fmtE(new Date(now)) };
      case 'yesterday': {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        return { startDate: fmtS(y), endDate: fmtE(new Date(y)) };
      }
      case 'this_week': {
        const d = now.getDay(), diff = now.getDate() - d + (d === 0 ? -6 : 1);
        const s = new Date(now); s.setDate(diff);
        return { startDate: fmtS(s), endDate: fmtE(new Date(now)) };
      }
      case 'last_week': {
        const s = new Date(now); s.setDate(s.getDate() - 7);
        return { startDate: fmtS(s), endDate: fmtE(new Date(now)) };
      }
      case 'this_month':
        return {
          startDate: fmtS(new Date(now.getFullYear(), now.getMonth(), 1)),
          endDate:   fmtE(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        };
      case 'last_month':
        return {
          startDate: fmtS(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
          endDate:   fmtE(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
      case 'this_year':
        return {
          startDate: fmtS(new Date(now.getFullYear(), 0, 1)),
          endDate:   fmtE(new Date(now.getFullYear(), 11, 31)),
        };
      case 'custom':
        return { startDate: customStart || '', endDate: customEnd || '' };
      default:
        return { startDate: '', endDate: '' };
    }
  };

  const fetchVerifiedOrders = async ({
    page            = currentPage - 1,
    overrideFilters = undefined,
    overrideSearch  = undefined,
    overrideSize    = undefined,
  } = {}) => {
    try {
      setLoading(true);

      const filters = overrideFilters !== undefined ? overrideFilters : activeFiltersRef.current;
      const search  = overrideSearch  !== undefined ? overrideSearch  : searchRef.current;
      const size    = overrideSize    !== undefined ? overrideSize    : orderLimit;

      const params = new URLSearchParams();

      if (search?.trim()) params.append('searchTerm', search.trim());
      if (selectedCourier !== 'All courier') params.append('courier', selectedCourier);
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.teamId) params.append('teamId', filters.teamId);
      if (filters.paymentMode) params.append('paymentMode', filters.paymentMode);
      if (filters.campaignName) params.append('campaignName', filters.campaignName);
      if (filters.category) params.append('category', filters.category);

      const resolved = filters.datePreset
        ? resolveDatePreset(filters.datePreset, filters.startDate, filters.endDate)
        : { startDate: filters.startDate || '', endDate: filters.endDate || '' };

      if (resolved.startDate) params.append('startDate', resolved.startDate);
      if (resolved.endDate)   params.append('endDate',   resolved.endDate);
      if (resolved.startDate || resolved.endDate) params.append('dateType', 'createdDate');

      params.append('page', page);
      params.append('size', overrideSize === 99999 ? totalOrders || 10000 : size);

      const isPhoneNumber = /^[6-9]\d{9}$/.test(search?.trim() || '');
      let data;

      if (isPhoneNumber) {
        const smartParams = new URLSearchParams();
        smartParams.append('searchTerm', search.trim());
        data = await ordersApi.smartSearch(Object.fromEntries(smartParams));
      } else {
        data = await ordersApi.getDispatchOrders(Object.fromEntries(params));
      }

      const list  = Array.isArray(data)          ? data
                  : Array.isArray(data?.orders)  ? data.orders
                  : Array.isArray(data?.content) ? data.content
                  : Array.isArray(data?.data)    ? data.data
                  : [];
      const total = data?.totalCount ?? data?.totalElements ?? data?.total ?? list.length;

      const phoneFlag = data?.isGlobalPhoneSearch === true || isPhoneNumber;
      setIsGlobalPhoneSearch(phoneFlag);
      setGlobalPhoneNumber(phoneFlag ? (search?.trim() || '') : '');

      setOrders(list);
      setTotalOrders(total);
    } catch (err) {
      console.error('Error fetching dispatch orders:', err);
      message.error('Failed to fetch dispatch orders');
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    setFilterOptionsLoading(true);
    try {
      const [empRes, catRes, campRes] = await Promise.allSettled([
        employeesApi.getAllEmployees(),
        productsApi.getAllCategories(),
        orderCampaignApi.getAllCampaigns(),
      ]);

      const empRaw = empRes.status === 'fulfilled' ? empRes.value : [];
      const empArr = Array.isArray(empRaw) ? empRaw
        : Array.isArray(empRaw?.data)    ? empRaw.data
        : Array.isArray(empRaw?.content) ? empRaw.content : [];
      const employees = empArr.map(e => ({
        name:         e.name || e.employeeName || e.fullName || '',
        id:           e.id,
        teamLeadName: e.teamLeadName || e.teamLead || '',
        teamId:       e.teamId,
      })).filter(e => e.name);

      const teamLeads = [...new Set(employees.map(e => e.teamLeadName).filter(Boolean))].sort();

      const catRaw = catRes.status === 'fulfilled' ? catRes.value : [];
      const catArr = Array.isArray(catRaw) ? catRaw : Array.isArray(catRaw?.data) ? catRaw.data : [];
      const categories = [...new Set(
        catArr.map(c => typeof c === 'string' ? c : c?.categoryName || c?.name).filter(Boolean)
      )].sort();

      const campRaw = campRes.status === 'fulfilled' ? campRes.value : [];
      const campArr = Array.isArray(campRaw) ? campRaw
        : Array.isArray(campRaw?.data)    ? campRaw.data
        : Array.isArray(campRaw?.content) ? campRaw.content : [];
      const campaigns = [...new Set(
        campArr.map(c => c?.campaignName || c?.name || c).filter(Boolean)
      )].sort();

      setFilterOptions({ employees, teamLeads, categories, campaigns });
    } catch (err) {
      console.error('fetchFilterOptions error:', err);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    activeFiltersRef.current = tempFilters;
    setCurrentPage(1);
    setIsFilterModalOpen(false);
    fetchVerifiedOrders({ page: 0, overrideFilters: tempFilters });
  };

  const handleResetFilters = () => {
    setTempFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    activeFiltersRef.current = emptyFilters;
    setCurrentPage(1);
    setIsFilterModalOpen(false);
    fetchVerifiedOrders({ page: 0, overrideFilters: emptyFilters });
  };

  const handlePageChange = (p) => {
    setCurrentPage(p);
    fetchVerifiedOrders({ page: p - 1 });
  };

  const handleSelectAll   = (e) => setSelectedOrders(e.target.checked ? orders.map(o => o.id) : []);
  const handleSelectOrder = (id) => setSelectedOrders(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleViewDetails = (order) => {
    navigate(`/orders/amount-decrease/${order.id}`, {
      state: { activeTab: 'VERIFIED', from: location.pathname }
    });
  };

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      if (!orders.length) { message.warning('No orders to export'); return; }

      const excelData = orders.map(order => ({
        'Order ID':     order.orderId || order.id,
        'Date':         order.orderDate ? formatDate(order.orderDate) : 'N/A',
        'Customer Name':order.customerName || 'N/A',
        'Phone':        order.phone || order.contactNumber || 'N/A',
        'Product':      getProductName(order),
        'Category':     order.category || 'N/A',
        'Amount':       order.restAmount || order.grandTotal || 0,
        'Payment Mode': order.paymentMode || 'COD',
        'Campaign':     order.orderCampaign || 'N/A',
        'Order By':     order.createdByName || 'N/A',
        'Verified By':  order.verifiedBy || 'N/A',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook  = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dispatch Ready Orders');
      XLSX.writeFile(workbook, `Dispatch_Ready_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success('Excel file downloaded successfully');
    } catch (err) {
      console.error('Excel download error:', err);
      message.error('Failed to download excel file.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenBulkDispatchModal = () => {
    if (!selectedOrders.length) { message.warning('Please select at least one order to dispatch'); return; }
    setBulkDispatchResults(null);
    setSrStep(SR_STEPS.CREATE);
    setSrCreatedOrders([]);
    setSrCourierOptions([]);
    setSrSelectedCourierId(null);
    setSrSelectedCourierName('');
    setBulkDispatchCourier('delhivery');
    nsReasonRef.current      = '';
    nsPinCodeRef.current     = '';
    nsCourierNameRef.current = '';
    setNsHasReason(false);
    setShowBulkDispatchModal(true);
    const awbs = {};
    selectedOrders.forEach(id => { awbs[id] = ''; });
    setBulkDispatchAwbs(awbs);
  };

  const SR_BASE   = ASSET_URL;
  const srHeaders = () => ({
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
  });

  const handleShiprocketCreateOrders = async () => {
    const ordersToDispatch = orders.filter(o => selectedOrders.includes(o.id));
    setSrStep(SR_STEPS.CREATE);
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: ordersToDispatch.length });
    const created = [], failed = [];

    for (let i = 0; i < ordersToDispatch.length; i++) {
      const order = ordersToDispatch[i];
      try {
        const orderDate = new Date().toISOString().slice(0, 16).replace('T', ' ');
        const isCOD      = ['COD', 'CASH'].includes(order.paymentMode);
        const isPrepaid  = ['ONLINE', 'PREPAID', 'UPI', 'CARD'].includes(order.paymentMode);
        const isPartPay  = order.paymentType === 'PART_PAYMENT';
        const pendingAmt = Math.max(0,
            parseFloat(order.pendingAmount) ||
            parseFloat(order.restAmount)    ||
            ((parseFloat(order.grandTotal) || 0) - (parseFloat(order.discount) || 0) - (parseFloat(order.prepaidAmount) || 0))
        );
        const paymentMethod = (isPrepaid && !isPartPay) ? 'Prepaid' : 'COD';
        const subTotal = isPartPay ? pendingAmt : (parseFloat(order.grandTotal) || 500);
        const codAmount = paymentMethod === 'COD'
            ? (isCOD ? (parseFloat(order.grandTotal) || 0) : pendingAmt)
            : 0;

        const payload = {
          order_id:              String(order.orderId || order.id),
          order_date:            orderDate,
          pickup_location:       'Home',
          billing_customer_name: order.customerName?.split(' ')[0] || 'Customer',
          billing_last_name:     order.customerName?.split(' ').slice(1).join(' ') || '',
          billing_address:       order.address || 'Address',
          billing_city:          order.district || 'Delhi',
          billing_pincode:       String(order.areaPinCode || '110001'),
          billing_state:         order.state || 'Delhi',
          billing_country:       'India',
          billing_email:         order.email || 'noreply@indharahealthcare.com',
          billing_phone:         String(order.contactNumber || '9999999999'),
          shipping_is_billing:   true,
          payment_method:        paymentMethod,
          sub_total:             subTotal,
          cod_amount:            codAmount,
          length: 15, breadth: 12, height: 6, weight: 0.2,
          order_items: (order.orderItems?.length > 0 ? order.orderItems : [order]).map(item => ({
            name:          item.productName || 'Product',
            sku:           String(item.sku || item.productId || 'SKU-001'),
            units:         item.quantity || 1,
            selling_price: item.unitPrice || item.grandTotal || order.grandTotal || 500,
            hsn:           item.hsnCode || '62091',
          })),
        };

        const response = await fetch(`${SR_BASE}/api/shiprocket/order`, {
          method: 'POST', headers: srHeaders(), body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || result.message || 'Order create failed');

        const shipmentId = result.shipment_id || result.order_id;
        if (!shipmentId) throw new Error('No shipment_id returned from Shiprocket');

        created.push({
          orderId:      order.orderId || order.id,
          internalId:   order.id,
          shipmentId:   String(shipmentId),
          customerName: order.customerName,
          pincode:      String(order.areaPinCode || '110001'),
          cod:          order.paymentMode === 'COD' ? 1 : 0,
        });
      } catch (err) {
        failed.push({ orderId: order.orderId || order.id, status: 'failed', message: err.message });
      }
      setDispatchProgress(prev => ({ ...prev, completed: i + 1 }));
    }

    setIsDispatchingBulk(false);
    if (created.length === 0) {
      message.error('All orders failed on Shiprocket');
      setBulkDispatchResults({ results: failed, successCount: 0, failureCount: failed.length });
      setSrStep(SR_STEPS.DONE);
      return;
    }
    if (failed.length > 0) message.warning(`${failed.length} failed. Proceeding with ${created.length}.`);
    setSrCreatedOrders(created);
    await fetchShiprocketCouriers(created);
  };

  const fetchShiprocketCouriers = async (createdList) => {
    setSrStep(SR_STEPS.PICK);
    setSrLoadingCouriers(true);
    setSrCourierOptions([]);
    try {
      const ref    = createdList[0];
      const params = new URLSearchParams({ pickup: '110045', delivery: ref.pincode, weight: '0.5', cod: String(ref.cod) });
      const response = await fetch(`${SR_BASE}/api/shiprocket/serviceability?${params}`, { headers: srHeaders() });
      const result   = await response.json();
      if (!response.ok) throw new Error(result.error || result.message || 'Serviceability failed');
      const couriers = result?.data?.available_courier_companies || result?.available_courier_companies || [];
      if (couriers.length === 0) throw new Error('No couriers available for this pincode');
      const sorted = [...couriers].sort((a, b) => {
        if (a.recommended && !b.recommended) return -1;
        if (!a.recommended && b.recommended) return 1;
        return (a.rate || 0) - (b.rate || 0);
      });
      setSrCourierOptions(sorted);
    } catch (err) {
      message.error(err.message || 'Failed to fetch couriers');
      setSrCourierOptions([]);
    } finally {
      setSrLoadingCouriers(false);
    }
  };

  const handleShiprocketAssignCourier = async () => {
    if (!srSelectedCourierId) { message.warning('Please select a courier'); return; }
    const alreadyDone = srCreatedOrders.filter(item => {
      const order = orders.find(o => o.id === item.internalId);
      return order && ['SHIPPED', 'IN_TRANSIT', 'DELIVERED'].includes(order.status);
    });
    if (alreadyDone.length > 0) message.warning(`${alreadyDone.length} order(s) already shipped — skipping.`);
    const toProcess = srCreatedOrders.filter(item => {
      const order = orders.find(o => o.id === item.internalId);
      return !order || !['SHIPPED', 'IN_TRANSIT', 'DELIVERED'].includes(order.status);
    });
    if (toProcess.length === 0) {
      message.info('All selected orders are already shipped.');
      setSrStep(SR_STEPS.DONE);
      setBulkDispatchResults({ results: [], successCount: 0, failureCount: 0 });
      return;
    }

    setSrStep(SR_STEPS.DISPATCHING);
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: toProcess.length });
    const results = [];
    let successCount = 0, failureCount = 0;

    for (let i = 0; i < toProcess.length; i++) {
      const item = toProcess[i];
      try {
        const awbRes = await fetch(
          `${SR_BASE}/api/shiprocket/awb?shipmentId=${item.shipmentId}&courierId=${srSelectedCourierId}`,
          { method: 'POST', headers: srHeaders() }
        );
        const awbResult = await awbRes.json();
        if (!awbRes.ok) throw new Error(awbResult.error || awbResult.message || 'AWB generation failed');
        const awb = awbResult?.awbCode || awbResult?.awb_code || awbResult?.response?.data?.awb_code || awbResult?.awb || '';
        if (!awb) throw new Error('No AWB returned — try a different courier');

        const dispatchData = {
          courierName:          srSelectedCourierName,
          trackingNumber:       awb,
          awbNumber:            awb,
          expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          remarks:              `Shiprocket — ${srSelectedCourierName}`,
          courierKey:           'shiprocket',
        };
        await ordersApi.dispatchOrder(item.internalId, dispatchData);
        results.push({ orderId: item.orderId, status: 'success', waybill: awb, message: `Via ${srSelectedCourierName}` });
        successCount++;
      } catch (err) {
        results.push({ orderId: item.orderId, status: 'failed', message: err.message });
        failureCount++;
      }
      setDispatchProgress(prev => ({ ...prev, completed: i + 1 }));
    }

    setIsDispatchingBulk(false);
    setSrStep(SR_STEPS.DONE);
    setBulkDispatchResults({ results, successCount, failureCount });
    fetchVerifiedOrders({ page: 0 });
    if (failureCount === 0) setSelectedOrders([]);
  };

  const handleDelhiveryBulkDispatch = async () => {
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: selectedOrders.length });
    const results = [];
    let successCount = 0, failureCount = 0;

    try {
      const ordersToDispatch = orders.filter(o => selectedOrders.includes(o.id));
      const chunkSize = 3;

      for (let i = 0; i < ordersToDispatch.length; i += chunkSize) {
        const chunk = ordersToDispatch.slice(i, i + chunkSize);
        await Promise.all(chunk.map(async (order) => {
          try {
            const payload = {
              shipments: [{
                name:          order.customerName || 'Customer',
                add:           order.address || 'Address',
                pin:           order.areaPinCode || '560001',
                city:          order.district || 'City',
                state:         order.state || 'State',
                country:       'India',
                phone:         order.contactNumber || '9999999999',
                order:         order.orderId || order.id || 'ORD123',
                paymentMode:   order.paymentMode,
                returnPin:     '110045',
                returnCity:    'New Delhi',
                returnPhone:   '7004269726',
                returnAdd:     'A-50, 1st Floor, Dashrathpuri',
                returnState:   'Delhi',
                returnCountry: 'India',
                productsDesc:  'Product',
                hsnCode:       '1302',
                codAmount:     (String(order.paymentMode).toUpperCase().includes('COD') || ((order.grandTotal || 0) - (order.prepaidAmount || 0)) > 0)
                  ? Math.max(0, (order.grandTotal - (order.prepaidAmount || 0))).toString()
                  : '0',
                totalAmount:   order.grandTotal?.toString() || '500',
              }],
              sellerAdd:      'A-50, 1st Floor, Dashrathpuri, New Delhi - 110045',
              sellerName:     'INDHARA HEALTHCARE PRIVATE LIMITED',
              sellerGstTin:   '07AAHCI7924B1ZO',
              sellerInv:      'INV-1769773320183',
              pickupLocation: { name: 'INDHARA HEALTHCARE PVT LTD' },
            };

            const response = await fetch(`${API_URL}/shipment/create`, {
              method:  'POST',
              headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              },
              body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || `API Error: ${response.status}`);

            const finalAwb = result.packages?.[0]?.waybill || result.waybill || result.awb || '';
            if (!finalAwb) throw new Error('No waybill generated from Delhivery');

            const dispatchData = {
              courierName:          'delhivery',
              trackingNumber:       finalAwb,
              awbNumber:            finalAwb,
              expectedDeliveryDate: bulkDispatchDate
                ? bulkDispatchDate.format('YYYY-MM-DD')
                : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              remarks:  bulkDispatchRemarks || 'Bulk dispatched via Delhivery',
              courierKey: 'delhivery',
            };
            await ordersApi.dispatchOrder(order.id, dispatchData);
            results.push({ orderId: order.orderId, status: 'success', waybill: finalAwb, message: 'Dispatched successfully' });
            successCount++;
          } catch (err) {
            results.push({ orderId: order.orderId, status: 'failed', message: err.message });
            failureCount++;
          }
        }));
        setDispatchProgress(prev => ({ ...prev, completed: Math.min(prev.completed + chunk.length, ordersToDispatch.length) }));
      }

      setBulkDispatchResults({ results, successCount, failureCount });
      fetchVerifiedOrders({ page: 0 });
      if (failureCount === 0) setSelectedOrders([]);
    } catch (err) {
      console.error('Bulk dispatch error:', err);
      message.error('An error occurred during bulk dispatch');
    } finally {
      setIsDispatchingBulk(false);
    }
  };

  const handleEkartDispatch = async () => {
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: selectedOrders.length });
    const results = [];
    let successCount = 0, failureCount = 0;
    try {
      const ordersToDispatch = orders.filter(o => selectedOrders.includes(o.id));
      for (let i = 0; i < ordersToDispatch.length; i++) {
        const order = ordersToDispatch[i];
        let finalAwb = '';
        try {
          const today = new Date().toISOString().split('T')[0];
          const payload = {
            order_number:              order.orderId || String(order.id),
            invoice_number:            order.orderId || String(order.id),
            invoice_date:              today,
            consignee_name:            order.customerName  || 'Customer',
            consignee_alternate_phone: order.alternateNumber || '9999999999',
            products_desc:             order.orderItems?.[0]?.productName || order.productName || 'Health Supplement',
            payment_mode:              order.paymentMode,
            category_of_goods:         order.category || 'Health',
            total_amount:              parseFloat(order.grandTotal) || 0,
            tax_value:                 0.0,
            taxable_amount:            parseFloat(order.grandTotal) || 0,
            commodity_value:           String(parseFloat(order.grandTotal) || 0),
            cod_amount:                order.paymentMode === 'COD'
                                         ? parseFloat((order.grandTotal - (order.prepaidAmount || 0)).toFixed(2))
                                         : 0,
            quantity:                  order.orderItems?.[0]?.quantity || 1,
           weight:                    200,   // 0.2 KG in grams
length:                    15,
height:                    6,
width:                     12,
            seller_name:               'Aarogya India Pvt Ltd',
            seller_address:            'Building No 23, Second Basement E Block, Sector 63, Noida',
            seller_gst_tin:            '09AAAAA0000A1Z5',
            consignee_gst_amount:      0.0,
            drop_location: {
              location_type: 'Home',
              address:       order.address || [order.houseNumber, order.streetName, order.colonyName, order.district].filter(Boolean).join(', ') || 'Address',
              city:          order.cityTown || order.district || 'City',
              state:         order.state    || 'State',
              country:       'India',
              name:          order.customerName  || 'Customer',
              phone:         parseInt(order.contactNumber) || 9999999999,
              pin:           parseInt(order.areaPinCode)   || 110001,
            },
          };
          const response = await fetch(`${API_URL}/courier/ekart/shipment/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}` },
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            let errMsg = errorData.message || errorData.error || `Ekart API Error: ${response.status}`;
            try { const match = errMsg.match(/Ekart API error: (.+)/); if (match) { const parsed = JSON.parse(match[1]); errMsg = parsed.description || parsed.message || errMsg; } } catch (_) {}
            throw new Error(errMsg);
          }
          const result = await response.json();
          finalAwb = result.awbNumber || result.tracking_number || result.waybill || result.awb || result.shipment_id || '';
          if (!finalAwb) throw new Error('No AWB number returned from Ekart');
          const dispatchData = { courierName: 'Ekart', trackingNumber: finalAwb, awbNumber: finalAwb, expectedDeliveryDate: bulkDispatchDate ? bulkDispatchDate.format('YYYY-MM-DD') : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], remarks: bulkDispatchRemarks || 'Bulk dispatched via Ekart', courierKey: 'ekart' };
          await ordersApi.dispatchOrder(order.id, dispatchData);
          results.push({ orderId: order.orderId, status: 'success', waybill: finalAwb, message: 'Dispatched successfully' });
          successCount++;
        } catch (err) {
          let errMsg = err.message;
          try { const match = errMsg.match(/Ekart API error: (.+)/); if (match) { const parsed = JSON.parse(match[1]); errMsg = parsed.description || parsed.message || errMsg; } } catch (_) {}
          results.push({ orderId: order.orderId, status: 'failed', message: errMsg });
          failureCount++;
        }
        setDispatchProgress(prev => ({ ...prev, completed: i + 1 }));
      }
      setBulkDispatchResults({ results, successCount, failureCount });
      fetchVerifiedOrders({ page: 0 });
      if (failureCount === 0) setSelectedOrders([]);
    } catch (err) {
      console.error('Ekart dispatch error:', err);
      message.error('An error occurred during Ekart dispatch');
    } finally {
      setIsDispatchingBulk(false);
    }
  };

  // ── FIXED: FShip — part payment check added ───────────────────────────────
  const handleFshipDispatch = async () => {
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: selectedOrders.length });
    const results = [];
    let successCount = 0, failureCount = 0;
    try {
      const ordersToDispatch = orders.filter(o => selectedOrders.includes(o.id));
      for (let i = 0; i < ordersToDispatch.length; i++) {
        const order = ordersToDispatch[i];
        try {
          // ── Part payment fix ──
          const { isCodMode, codAmount } = resolvePaymentInfo(order);
         const fshipPaymentMode = isCodMode ? 1 : 2;
          // ─────────────────────

          const payload = {
            customer_Name:        order.customerName || 'Customer',
            customer_Mobile:      order.contactNumber || '9999999999',
            customer_Emailid:     order.email || 'noreply@aarogyaindia.com',
            customer_Address:     order.address,
            landMark:             order.landmark || 'NA',
            customer_Address_Type:'Home',
            customer_PinCode:     order.areaPinCode || '110001',
            customer_City:        order.district || 'City',
            orderId:              order.orderId || order.id,
            invoice_Number:       order.orderId || order.id,
            payment_Mode:         fshipPaymentMode,   // ← fixed
            express_Type:         'surface',
            is_Ndd:               0,
            order_Amount:         order.grandTotal || 0,
            tax_Amount:           0,
            extra_Charges:        0,
            total_Amount:         order.grandTotal || 0,
            cod_Amount:           codAmount,           // ← fixed
            shipment_Weight:      0.2,
shipment_Length:      15.0,
shipment_Width:       12.0,
shipment_Height:      6.0,
volumetric_Weight:    0.22,
            pick_Address_ID:      220957,
            return_Address_ID:    220957,
            courierId:            59,
            products: (order.orderItems?.length > 0 ? order.orderItems : [order]).map(item => ({
              productName:     item.productName || 'Product',
              unitPrice:       item.unitPrice || item.grandTotal || 0,
              quantity:        item.quantity || 1,
              productCategory: item.category || order.category || 'General',
              hsnCode:         item.hsnCode || '30049099',
              sku:             String(item.sku || item.productId || 'SKU-001'),
              taxRate:         item.taxRate || 18.0,
            })),
          };

          const response = await fetch(`${API_URL}/fship/order/forward`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
            body: JSON.stringify(payload),
          });
          if (!response.ok) { const errorData = await response.json().catch(() => ({})); throw new Error(errorData.error || `FShip API Error: ${response.status}`); }
          const result = await response.json();
          const finalAwb = result.awbNumber || result.trackingNumber || result.waybill || '';
          if (!finalAwb) throw new Error('No AWB number returned from FShip');
          const dispatchData = { courierName: 'FShip', trackingNumber: finalAwb, awbNumber: finalAwb, expectedDeliveryDate: bulkDispatchDate ? bulkDispatchDate.format('YYYY-MM-DD') : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], remarks: bulkDispatchRemarks || 'Bulk dispatched via FShip', courierKey: 'fship' };
          await ordersApi.dispatchOrder(order.id, dispatchData);
          results.push({ orderId: order.orderId, status: 'success', waybill: finalAwb, message: 'Dispatched successfully' });
          successCount++;
        } catch (err) {
          results.push({ orderId: order.orderId, status: 'failed', message: err.message });
          failureCount++;
        }
        setDispatchProgress(prev => ({ ...prev, completed: i + 1 }));
      }
      setBulkDispatchResults({ results, successCount, failureCount });
      fetchVerifiedOrders({ page: 0 });
      if (failureCount === 0) setSelectedOrders([]);
    } catch (err) {
      console.error('FShip dispatch error:', err);
      message.error('An error occurred during FShip dispatch');
    } finally {
      setIsDispatchingBulk(false);
    }
  };

  // ── FIXED: Shadowfax — part payment check added ───────────────────────────
  const handleShadowfaxDispatch = async () => {
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: selectedOrders.length });
    const results = [];
    let successCount = 0, failureCount = 0;
    try {
      const ordersToDispatch = orders.filter(o => selectedOrders.includes(o.id));
      for (let i = 0; i < ordersToDispatch.length; i++) {
        const order = ordersToDispatch[i];
        try {
          // ── Part payment fix ──
          const { isCodMode, codAmount } = resolvePaymentInfo(order);
          const sfxPaymentType = isCodMode ? 'COD' : 'PREPAID';
          // ─────────────────────

          const payload = {
            order_id:         `SFX-${order.orderId || order.id}`,
            order_type:       'forward',
            payment_type:     sfxPaymentType,   // ← fixed
            cod_amount:       codAmount,         // ← fixed
            invoice_value:    order.grandTotal || 0,
            invoice_number:   order.orderId || order.id,
            product_name:     order.orderItems?.[0]?.productName || order.productName || 'Product',
            product_category: order.category || 'General',
            hsn_code:         order.orderItems?.[0]?.hsnCode || '30049099',
          package_weight:   200,   // 0.2 KG in grams
package_length:   15.0,
package_breadth:  12.0,
package_height:   6.0,
            customer_name:    order.customerName || 'Customer',
            customer_phone:   order.contactNumber || '9999999999',
            customer_address: order.address || 'Address',
            customer_city:    order.district || 'City',
            customer_state:   order.state || 'State',
            customer_pincode: order.areaPinCode || '110001',
          };

          const response = await fetch(`${API_URL}/shadowfax/order/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
            body: JSON.stringify(payload),
          });
          if (!response.ok) { const errorData = await response.json().catch(() => ({})); throw new Error(errorData.error || `Shadowfax API Error: ${response.status}`); }
          const result = await response.json();
          const finalAwb = result.awb_number || result.tracking_number || result.waybill || result.order_id || '';
          if (!finalAwb) throw new Error('No AWB number returned from Shadowfax');
          const dispatchData = { courierName: 'Shadowfax', trackingNumber: finalAwb, awbNumber: finalAwb, expectedDeliveryDate: bulkDispatchDate ? bulkDispatchDate.format('YYYY-MM-DD') : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], remarks: bulkDispatchRemarks || 'Bulk dispatched via Shadowfax', courierKey: 'shadowfax' };
          await ordersApi.dispatchOrder(order.id, dispatchData);
          results.push({ orderId: order.orderId, status: 'success', waybill: finalAwb, message: 'Dispatched successfully' });
          successCount++;
        } catch (err) {
          results.push({ orderId: order.orderId, status: 'failed', message: err.message });
          failureCount++;
        }
        setDispatchProgress(prev => ({ ...prev, completed: i + 1 }));
      }
      setBulkDispatchResults({ results, successCount, failureCount });
      fetchVerifiedOrders({ page: 0 });
      if (failureCount === 0) setSelectedOrders([]);
    } catch (err) {
      console.error('Shadowfax dispatch error:', err);
      message.error('An error occurred during Shadowfax dispatch');
    } finally {
      setIsDispatchingBulk(false);
    }
  };

  // ── FIXED: ParcelX — part payment check added ─────────────────────────────
  const handleParcelXDispatch = async () => {
    setIsDispatchingBulk(true);
    setDispatchProgress({ completed: 0, total: selectedOrders.length });
    const results = [];
    let successCount = 0, failureCount = 0;
    try {
      const ordersToDispatch = orders.filter(o => selectedOrders.includes(o.id));
      for (let i = 0; i < ordersToDispatch.length; i++) {
        const order = ordersToDispatch[i];
        try {
          // ── Part payment fix ──
          const { isCodMode, codAmount } = resolvePaymentInfo(order);
          const pxPaymentMode = isCodMode ? 'COD' : 'PREPAID';
          // ─────────────────────

          const payload = {
            consignee_name:     order.customerName || 'Customer',
            consignee_address1: order.address,
            consignee_pincode:  String(order.areaPinCode || '110001'),
            consignee_mobile:   String(order.contactNumber || '9999999999'),
            client_order_id:    String(order.orderId || order.id),
            invoice_number:     String(order.orderId || order.id),
            payment_mode:       pxPaymentMode,   // ← fixed
            cod_amount:         codAmount,        // ← fixed
            order_amount:       String(order.grandTotal || 0),
            courier_type:       1,
            courier_code:       'PXXBS02',
            pick_address_id:    '98795',
            products: (order.orderItems?.length > 0 ? order.orderItems : [order]).map(item => ({
              product_sku:      String(item.sku || item.productId || 'SKU-001'),
              product_name:     item.productName || 'Product',
              product_value:    String(item.unitPrice || item.grandTotal || order.grandTotal || 0),
              product_quantity: String(item.quantity || 1),
            })),
          };

          const response = await fetch(`${API_URL}/parcelx/order/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}` },
            body: JSON.stringify(payload),
          });
          if (!response.ok) { const errorData = await response.json().catch(() => ({})); throw new Error(errorData.message || errorData.error || `ParcelX API Error: ${response.status}`); }
          const result = await response.json();
          const finalAwb = result.awbNumber || result.awb_number || result.tracking_number || result.waybill || result.awb || result.shipment_id || '';
          if (!finalAwb) throw new Error('No AWB number returned from ParcelX');
          const dispatchData = { courierName: 'ParcelX', trackingNumber: finalAwb, awbNumber: finalAwb, expectedDeliveryDate: bulkDispatchDate ? bulkDispatchDate.format('YYYY-MM-DD') : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], remarks: bulkDispatchRemarks || 'Bulk dispatched via ParcelX', courierKey: 'parcelx' };
          await ordersApi.dispatchOrder(order.id, dispatchData);
          results.push({ orderId: order.orderId, status: 'success', waybill: finalAwb, message: 'Dispatched successfully' });
          successCount++;
        } catch (err) {
          results.push({ orderId: order.orderId, status: 'failed', message: err.message });
          failureCount++;
        }
        setDispatchProgress(prev => ({ ...prev, completed: i + 1 }));
      }
      setBulkDispatchResults({ results, successCount, failureCount });
      fetchVerifiedOrders({ page: 0 });
      if (failureCount === 0) setSelectedOrders([]);
    } catch (err) {
      console.error('ParcelX dispatch error:', err);
      message.error('An error occurred during ParcelX dispatch');
    } finally {
      setIsDispatchingBulk(false);
    }
  };

  // ── NS List fetch ─────────────────────────────────────────────────────────
  const fetchNsOrders = async (page = 0) => {
    setNsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', 10);
      const response = await fetch(`${API_URL}/workflow/not-serviceable?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!response.ok) throw new Error(`NS List Error: ${response.status}`);
      const data = await response.json();
      const list  = Array.isArray(data)         ? data
                  : Array.isArray(data?.content) ? data.content
                  : Array.isArray(data?.orders)  ? data.orders
                  : Array.isArray(data?.data)    ? data.data : [];
      const total = data?.totalCount ?? data?.totalElements ?? data?.total ?? list.length;
      setNsOrders(list);
      setNsTotalItems(total);
    } catch (err) {
      console.error('NS list fetch error:', err);
      setNsOrders([]);
    } finally {
      setNsLoading(false);
    }
  };

  const handleRequeueNs = async (orderId) => {
    setRequeuingId(orderId);
    try {
      const response = await fetch(`${API_URL}/workflow/not-serviceable/${orderId}/re-queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || e.error || 'Re-queue failed'); }
      message.success('Order re-queued for dispatch!');
      fetchNsOrders(nsCurrentPage - 1);
      fetchVerifiedOrders({ page: 0 });
    } catch (err) {
      message.error(err.message || 'Failed to re-queue order');
    } finally {
      setRequeuingId(null);
    }
  };

  const handleNotServiceableBulk = async () => {
    const nsReason      = nsReasonRef.current;
    const nsPinCode     = nsPinCodeRef.current;
    const nsCourierName = nsCourierNameRef.current;
    if (!nsReason.trim()) { message.warning('Please enter a reason for NS'); return; }
    setIsMarkingNS(true);
    const results = [];
    let successCount = 0, failureCount = 0;
    try {
      const user = getCurrentUser();
      const markedByName = user?.name || 'Agent';

      if (selectedOrders.length === 1) {
        try {
          const response = await fetch(`${API_URL}/workflow/not-serviceable/${selectedOrders[0]}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
            body: JSON.stringify({
              reason: nsReason,
              ...(nsPinCode     && { pinCode:     nsPinCode     }),
              ...(nsCourierName && { courierName: nsCourierName }),
              markedByName,
            }),
          });
          if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || e.error || `NS Error: ${response.status}`); }
          const order = orders.find(o => o.id === selectedOrders[0]);
          results.push({ orderId: order?.orderId || selectedOrders[0], status: 'success', message: 'Marked as Not Serviceable' });
          successCount++;
        } catch (err) {
          const order = orders.find(o => o.id === selectedOrders[0]);
          results.push({ orderId: order?.orderId || selectedOrders[0], status: 'failed', message: err.message });
          failureCount++;
        }
      } else {
        try {
          const response = await fetch(`${API_URL}/workflow/not-serviceable/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
            body: JSON.stringify({
              orderIds: selectedOrders,
              reason: nsReason,
              ...(nsPinCode     && { pinCode:     nsPinCode     }),
              ...(nsCourierName && { courierName: nsCourierName }),
              markedByName,
            }),
          });
          if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || e.error || `Bulk NS Error: ${response.status}`); }
          successCount = selectedOrders.length;
          selectedOrders.forEach(id => {
            const order = orders.find(o => o.id === id);
            results.push({ orderId: order?.orderId || id, status: 'success', message: 'Marked as Not Serviceable' });
          });
        } catch (err) {
          failureCount = selectedOrders.length;
          selectedOrders.forEach(id => {
            const order = orders.find(o => o.id === id);
            results.push({ orderId: order?.orderId || id, status: 'failed', message: err.message });
          });
        }
      }

      setBulkDispatchResults({ results, successCount, failureCount });
      if (successCount > 0) {
        message.success(`${successCount} order(s) marked as Not Serviceable`);
        fetchVerifiedOrders({ page: 0 });
        fetchNsOrders(0);
        setNsCurrentPage(1);
        if (failureCount === 0) setSelectedOrders([]);
      }
    } catch (err) {
      console.error('NS bulk error:', err);
      message.error('Failed to mark orders as Not Serviceable');
    } finally {
      setIsMarkingNS(false);
    }
  };

  const handleBulkDispatch = () => {
    if (bulkDispatchCourier === 'not_serviceable') { handleNotServiceableBulk(); return; }
    if (bulkDispatchCourier === 'shiprocket')       handleShiprocketCreateOrders();
    else if (bulkDispatchCourier === 'fship')       handleFshipDispatch();
    else if (bulkDispatchCourier === 'shadowfax')   handleShadowfaxDispatch();
    else if (bulkDispatchCourier === 'ekart')       handleEkartDispatch();
    else if (bulkDispatchCourier === 'parcelx')     handleParcelXDispatch();
    else handleDelhiveryBulkDispatch();
  };

  const LoadingDot = () => (
    <span
      className="ml-1 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle"
      style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}
    />
  );

  const CourierCard = ({ courier }) => {
    const isSelected = srSelectedCourierId === courier.courier_company_id;
    const etaDays    = courier.estimated_delivery_days || courier.etd || '3-5';
    return (
      <div
        onClick={() => { setSrSelectedCourierId(courier.courier_company_id); setSrSelectedCourierName(courier.courier_name); }}
        className="border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md"
        style={{ borderColor: isSelected ? currentTheme.color : '#E5E7EB', backgroundColor: isSelected ? `${currentTheme.color}10` : '#FFFFFF' }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isSelected && (
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: currentTheme.color }}>
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
            )}
            <span className="font-bold text-gray-900 text-sm">{courier.courier_name}</span>
          </div>
          <span className="text-lg font-bold" style={{ color: currentTheme.color }}>₹{Math.round(courier.rate || 0)}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
          <div className="flex items-center gap-1"><Clock className="w-3 h-3"/><span>ETA: {etaDays} days</span></div>
          {courier.rating && <div className="flex items-center gap-1"><span>⭐</span><span>{courier.rating}/5</span></div>}
          {courier.is_surface && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium">Surface</span>}
          {courier.cod === 1 && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-medium">COD ✓</span>}
        </div>
        {courier.call_before_delivery === 1 && (
          <div className="mt-2 text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-block">📞 Call before delivery</div>
        )}
      </div>
    );
  };

  const BulkDispatchModal = () => {
    const isSR = bulkDispatchCourier === 'shiprocket';
    const isNS = bulkDispatchCourier === 'not_serviceable';
    return (
      <Modal
        title={bulkDispatchResults ? '📊 Dispatch Results' : isSR && srStep === SR_STEPS.PICK ? '🚚 Pick a Delivery Partner' : isNS ? '🚫 Mark as Not Serviceable' : 'Bulk Dispatch Orders'}
        open={showBulkDispatchModal}
        onCancel={() => !isDispatchingBulk && !isMarkingNS && setShowBulkDispatchModal(false)}
        footer={null}
        width={isSR && srStep === SR_STEPS.PICK ? 780 : 700}
        closable={!isDispatchingBulk && !isMarkingNS}
      >
        <div className="space-y-6">
          {isDispatchingBulk && (
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-blue-900">
                  {srStep === SR_STEPS.CREATE ? 'Creating orders on Shiprocket...' : 'Assigning AWB & dispatching...'}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-blue-700">{dispatchProgress.completed} of {dispatchProgress.total}</span>
                  <Loader className="w-5 h-5 animate-spin text-blue-600"/>
                </div>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-3 overflow-hidden">
                <div className="bg-blue-600 h-full transition-all duration-500 ease-out" style={{ width: `${(dispatchProgress.completed / dispatchProgress.total) * 100}%` }}/>
              </div>
            </div>
          )}

          {isMarkingNS && (
            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-red-900">Marking orders as Not Serviceable...</span>
                <Loader className="w-5 h-5 animate-spin text-red-600"/>
              </div>
            </div>
          )}

          {bulkDispatchResults ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600 font-medium">✅ Successful</p>
                  <p className="text-3xl font-bold text-green-700">{bulkDispatchResults.successCount}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600 font-medium">❌ Failed</p>
                  <p className="text-3xl font-bold text-red-700">{bulkDispatchResults.failureCount}</p>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto space-y-2 pr-2">
                {bulkDispatchResults.results.map((r, i) => (
                  <div key={i} className={`p-3 rounded-lg border flex items-start justify-between ${r.status==='success'?'bg-green-50/50 border-green-100':'bg-red-50/50 border-red-100'}`}>
                    <div>
                      <p className="font-bold text-gray-900">{r.orderId}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{r.message}</p>
                      {r.waybill && <p className="text-xs font-mono bg-white px-2 py-0.5 rounded border border-gray-100 inline-block mt-2">AWB: {r.waybill}</p>}
                    </div>
                    <span className="text-xl">{r.status==='success'?'✅':'❌'}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-4">
                <button onClick={() => setShowBulkDispatchModal(false)} className="px-8 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors font-medium">Close</button>
              </div>
            </div>
          ) : isSR && srStep === SR_STEPS.PICK ? (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-gray-600"><span className="font-semibold text-gray-900">{srCreatedOrders.length}</span> order(s) created on Shiprocket</span>
                <span className="text-green-600 font-medium text-xs">✓ Ready to assign courier</span>
              </div>
              {srLoadingCouriers ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader className="w-8 h-8 animate-spin" style={{ color: currentTheme.color }}/>
                  <p className="text-sm text-gray-500">Fetching available couriers...</p>
                </div>
              ) : srCourierOptions.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <p>No couriers available for this pincode.</p>
                  <button onClick={() => fetchShiprocketCouriers(srCreatedOrders)} className="mt-3 text-sm underline" style={{ color: currentTheme.color }}>Retry</button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500">{srCourierOptions.length} couriers available — sorted by price</p>
                  <div className="grid grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
                    {srCourierOptions.map(courier => <CourierCard key={courier.courier_company_id} courier={courier}/>)}
                  </div>
                </>
              )}
              <div className="flex items-center justify-between pt-4 border-t">
                <div>{srSelectedCourierId && <p className="text-sm text-gray-600">Selected: <span className="font-semibold" style={{ color: currentTheme.color }}>{srSelectedCourierName}</span></p>}</div>
                <div className="flex gap-3">
                  <button onClick={() => setShowBulkDispatchModal(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
                  <button onClick={handleShiprocketAssignCourier} disabled={!srSelectedCourierId || srLoadingCouriers}
                    className="px-8 py-2.5 text-white rounded-lg flex items-center gap-2 font-bold shadow-lg hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: currentTheme.color }}>
                    <Truck className="w-5 h-5"/>Assign &amp; Dispatch
                  </button>
                </div>
              </div>
            </div>
          ) : (
            !isDispatchingBulk && !isMarkingNS && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Select Courier Partner</label>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(COURIER_APIS).map(([key, courier]) => (
                      <button key={key} onClick={() => setBulkDispatchCourier(key)}
                        className="p-4 rounded-xl border-2 flex flex-col items-center space-y-3 transition-all hover:shadow-md"
                        style={{
                          borderColor:     bulkDispatchCourier === key ? (key === 'not_serviceable' ? '#ef4444' : currentTheme.color) : undefined,
                          backgroundColor: bulkDispatchCourier === key ? (key === 'not_serviceable' ? '#fef2f2' : `${currentTheme.color}10`) : undefined,
                        }}>
                        <span className="text-3xl">{courier.logo}</span>
                        <div className="text-center">
                          <p className="text-sm font-bold text-gray-900">{courier.name}</p>
                          {key === 'not_serviceable'
                            ? <span className="text-[10px] uppercase tracking-wider font-bold text-red-500 mt-1 block">Mark NS</span>
                            : <span className="text-[10px] uppercase tracking-wider font-bold text-green-600 mt-1 block">Auto AWB Enabled</span>
                          }
                          {key === 'shiprocket' && <span className="text-[10px] text-blue-500 mt-0.5 block">Pick delivery partner</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {isNS && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-3">
                    <p className="text-sm font-bold text-red-700">🚫 Not Serviceable Details</p>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Reason <span className="text-red-500">*</span></label>
                      <textarea
                        key="ns-reason"
                        defaultValue={nsReasonRef.current}
                        onChange={(e) => { nsReasonRef.current = e.target.value; setNsHasReason(e.target.value.trim().length > 0); }}
                        placeholder="e.g. Pin code not serviceable by courier"
                        rows={2}
                        className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Courier Name (optional)</label>
                        <input
                          key="ns-courier"
                          type="text"
                          defaultValue={nsCourierNameRef.current}
                          onChange={(e) => { nsCourierNameRef.current = e.target.value; }}
                          placeholder="e.g. Delhivery"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"/>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Pin Code (optional)</label>
                        <input
                          key="ns-pincode"
                          type="text"
                          defaultValue={nsPinCodeRef.current}
                          onChange={(e) => { nsPinCodeRef.current = e.target.value; }}
                          placeholder="e.g. 110092"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"/>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <button onClick={() => setShowBulkDispatchModal(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium">Cancel</button>
                  <button
                    onClick={handleBulkDispatch}
                    disabled={isNS && !nsHasReason}
                    className="px-8 py-2.5 text-white rounded-lg flex items-center justify-center space-x-2 font-bold shadow-lg hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: isNS ? '#ef4444' : currentTheme.color }}>
                    {isNS
                      ? <><span>🚫</span><span>Mark {selectedOrders.length} Order(s) NS</span></>
                      : <><Truck className="w-5 h-5"/><span>{bulkDispatchCourier === 'shiprocket' ? 'Next: Pick Courier →' : `Dispatch ${selectedOrders.length} Order(s)`}</span></>
                    }
                  </button>
                </div>
              </>
            )
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : ''}`}>
      <div className="space-y-6">
      <BulkDispatchModal/>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl desktop:text-3xl font-bold text-gray-800">
          Verified Orders <span className="text-gray-500 text-xl desktop:text-2xl">({totalOrders})</span>
        </h1>
        <div className="flex items-center space-x-3">
          {has('DISPATCH_VIEW') && <ExcelExportButton onClick={handleDownloadExcel} isDownloading={isDownloading}/>}
          {has('DISPATCH_VIEW') && (
            <button
              onClick={() => { setTempFilters(activeFilters); setIsFilterModalOpen(true); }}
              className="relative p-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all"
              style={{ color: currentTheme.color }}
              title="Filter"
            >
              <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-600"/>
              <span className="font-medium hidden desktop:inline">Filter</span>
              {Object.values(activeFilters).some(v => v !== '') && (
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: currentTheme.color }}/>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Page Tabs */}
      <div className={`flex items-center gap-1 p-1 rounded-xl w-fit ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
        <button
          onClick={() => setActivePageTab('dispatch')}
          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${activePageTab === 'dispatch' ? 'text-white shadow-sm' : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800')}`}
          style={{ backgroundColor: activePageTab === 'dispatch' ? currentTheme.color : 'transparent' }}
        >
          🚚 Dispatch Ready
          {totalOrders > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-black ${activePageTab === 'dispatch' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
              {totalOrders}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActivePageTab('ns'); fetchNsOrders(0); setNsCurrentPage(1); }}
          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${activePageTab === 'ns' ? 'text-white shadow-sm' : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800')}`}
          style={{ backgroundColor: activePageTab === 'ns' ? '#ef4444' : 'transparent' }}
        >
          🚫 Not Serviceable
          {nsTotalItems > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-black ${activePageTab === 'ns' ? 'bg-white/20 text-white' : 'bg-red-100 text-red-600'}`}>
              {nsTotalItems}
            </span>
          )}
        </button>
      </div>

      {/* Search + Limit */}
      <div className="flex items-center space-x-4" style={{ display: activePageTab === 'ns' ? 'none' : 'flex' }}>
        <ExpandableSearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} placeholder="Search by lead id or customer name"/>
        <select
          value={orderLimit}
          onChange={(e) => {
            const newLimit = parseInt(e.target.value);
            setOrderLimit(newLimit);
            setCurrentPage(1);
            fetchVerifiedOrders({ page: 0, overrideSize: newLimit });
          }}
          className="px-4 py-2.5 desktop:px-6 desktop:py-4 border border-gray-300 rounded-lg focus:outline-none transition-all desktop:text-lg"
          style={{ borderColor: currentTheme.color }}
        >
          <option value={20}>20 Orders</option>
          <option value={50}>50 Orders</option>
          <option value={100}>100 Orders</option>
          <option value={99999}>All</option>
        </select>
      </div>

      {/* Global Phone Search Banner */}
      {activePageTab === 'dispatch' && isGlobalPhoneSearch && (
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-medium"
          style={{ backgroundColor: `${currentTheme.color}12`, borderColor: `${currentTheme.color}40`, color: currentTheme.color }}>
          <div className="flex items-center gap-2">
            <span className="text-base">📞</span>
            <span>
              Showing all orders for number&nbsp;
              <span className="font-bold tracking-wide">{globalPhoneNumber}</span>
              &nbsp;— across all agents & teams
            </span>
          </div>
          <button
            onClick={() => { setSearchTerm(''); setIsGlobalPhoneSearch(false); setGlobalPhoneNumber(''); }}
            className="ml-4 p-1 rounded-full hover:bg-black/10 transition-colors"
            title="Clear phone search"
          >
            <X className="w-4 h-4"/>
          </button>
        </div>
      )}

      {/* Table + NS tab content */}
      {activePageTab === 'dispatch' ? (
        <>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-white" style={{ background: currentTheme.color }}>
              <tr>
                {has('DISPATCH_EDIT') && (
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left">
                    <Checkbox checked={selectedOrders.length === orders.length && orders.length > 0} onChange={handleSelectAll}/>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">ORDER ID</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">DATE</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">NAME</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">PRODUCT</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">CATEGORY</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">REST AMT.</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">PAY MODE</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">ORDER CAMPAIGN</th>
                <th className="px-4 py-3 text-left text-sm desktop:text-base font-medium whitespace-nowrap">ORDER BY</th>
                <th className="px-4 py-3 text-center text-sm desktop:text-base font-medium whitespace-nowrap">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="11" className="px-4 py-8 text-center">
                  <div className="inline-block w-6 h-6 border-4 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}/>
                </td></tr>
              ) : orders.length > 0 ? (
                orders.map((order, index) => (
                  <tr key={order.id}
                    className={`hover:bg-gray-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    style={{ backgroundColor: selectedOrders.includes(order.id) ? `${currentTheme.color}10` : undefined }}
                  >
                    {has('DISPATCH_EDIT') && (
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedOrders.includes(order.id)} onChange={() => handleSelectOrder(order.id)}/>
                      </td>
                    )}
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base font-medium text-gray-900 cursor-pointer transition-colors"
                      onMouseEnter={(e) => e.target.style.color = currentTheme.color}
                      onMouseLeave={(e) => e.target.style.color = 'inherit'}
                      onClick={() => handleViewDetails(order)}>
                      {order.orderId}
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700 whitespace-nowrap">
                      {order.orderDate ? formatDate(order.orderDate) : '-'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{order.customerName || order.lead?.customerName}</span>
                        <button
                          onClick={() => triggerDialer(order)}
                          className="flex items-center space-x-1 text-xs text-gray-500 transition-colors group w-fit mt-0.5"
                          onMouseEnter={(e) => e.currentTarget.style.color = currentTheme.color}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                          title={`Call ${order.customerName || order.lead?.customerName}`}
                        >
                          <span className="group-hover:underline">{(order.contactNumber || order.lead?.contactNumber)}</span>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700 whitespace-nowrap">{getProductName(order)}</td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700 whitespace-nowrap">{order.category || '-'}</td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base font-bold whitespace-nowrap" style={{ color: currentTheme.color }}>
                      ₹ {order.restAmount || order.grandTotal || 0}
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700 whitespace-nowrap">{order.paymentMode || 'COD'}</td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700 whitespace-nowrap">{order.orderCampaign || '-'}</td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base whitespace-nowrap">
                      <span className="font-medium text-gray-900">{order.createdByName || '-'}</span>
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleViewDetails(order)}
                        className="w-8 h-8 desktop:w-10 desktop:h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors">
                        <svg className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="11" className="px-4 py-8 text-center text-gray-500">No verified orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

          <Pagination currentPage={currentPage} totalItems={totalOrders} itemsPerPage={orderLimit} onPageChange={handlePageChange}/>
        </>
      ) : (
      <div className="mt-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-700">
              Not Serviceable Orders
              {nsTotalItems > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">({nsTotalItems})</span>
              )}
            </h2>
          </div>
          <button
            onClick={() => { setNsCurrentPage(1); fetchNsOrders(0); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            style={{ borderColor: '#ef4444', color: '#ef4444' }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            Refresh
          </button>
        </div>

        <div className="bg-white rounded-lg border border-red-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#ef4444' }}>
                <tr className="text-white text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Order ID</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Date</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Name</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Reason</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Courier</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Pin Code</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap font-semibold">Marked By</th>
                  <th className="px-4 py-3 text-center whitespace-nowrap font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-50">
                {nsLoading ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center">
                      <Loader className="w-5 h-5 animate-spin mx-auto" style={{ color: '#ef4444' }}/>
                    </td>
                  </tr>
                ) : nsOrders.length > 0 ? nsOrders.map((order, idx) => (
                  <tr key={order.id || order.orderId || idx}
                    className={`transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-red-50/30'} hover:bg-red-50/60`}>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {order.orderId || order.id || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {order.markedAt || order.createdAt || order.date
                        ? new Date(order.markedAt || order.createdAt || order.date)
                            .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                      {order.customerName || order.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-700 max-w-[200px]">
                      <p className="truncate" title={order.reason || ''}>{order.reason || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {order.courierName || order.courier || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap font-mono">
                      {order.pinCode || order.pincode || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {order.markedByName || order.markedBy || '—'}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <button
                        onClick={() => handleRequeueNs(order.id || order.orderId)}
                        disabled={requeueingId === (order.id || order.orderId)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white rounded-lg mx-auto transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                        style={{ backgroundColor: currentTheme.color }}
                        title="Re-queue for dispatch"
                      >
                        {requeueingId === (order.id || order.orderId)
                          ? <Loader className="w-3 h-3 animate-spin"/>
                          : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                        }
                        Re-queue
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-400 text-sm">
                      No Not Serviceable orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {nsTotalItems > 10 && (
            <div className="px-4 py-3 border-t border-red-50">
              <Pagination
                currentPage={nsCurrentPage}
                totalItems={nsTotalItems}
                itemsPerPage={10}
                onPageChange={(p) => { setNsCurrentPage(p); fetchNsOrders(p - 1); }}
              />
            </div>
          )}
        </div>
      </div>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsFilterModalOpen(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col shadow-xl max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: currentTheme.color }}>
              <h2 className="text-lg font-bold text-white">Filter Orders</h2>
              <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors">
                <X className="w-5 h-5 text-white"/>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-1">
                  <DateRangeFilter
                    label="Date Range"
                    placeholder="All Time"
                    preset={tempFilters.datePreset}
                    startDate={tempFilters.startDate}
                    endDate={tempFilters.endDate}
                    onChange={({ preset, startDate, endDate }) =>
                      setTempFilters({
                        ...tempFilters,
                        datePreset: preset,
                        startDate,
                        endDate,
                      })
                    }
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agent {filterOptionsLoading && <LoadingDot />}</label>
                  <ThemeSelect value={tempFilters.employeeName} onChange={(val) => { const emp = filterOptions.employees.find(e => e.name === val); setTempFilters({ ...tempFilters, employeeName: val, employeeId: emp?.id || '' }); }} options={[{ label: 'All Agents', value: '' }, ...filterOptions.employees.map(e => ({ label: e.name, value: e.name }))]} icon={Users} className="w-full" placeholder="All Agents"/>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Lead {filterOptionsLoading && <LoadingDot />}</label>
                  <ThemeSelect value={tempFilters.teamLeadName} onChange={(val) => { const emp = filterOptions.employees.find(e => e.teamLeadName === val); setTempFilters({ ...tempFilters, teamLeadName: val, teamId: emp?.teamId || '' }); }} options={[{ label: 'All Team Leads', value: '' }, ...filterOptions.teamLeads.map(t => ({ label: t, value: t }))]} icon={User} className="w-full" placeholder="All Team Leads"/>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category {filterOptionsLoading && <LoadingDot />}</label>
                  <ThemeSelect value={tempFilters.category} onChange={(val) => setTempFilters({ ...tempFilters, category: val })} options={[{ label: 'All Categories', value: '' }, ...filterOptions.categories.map(c => ({ label: c, value: c }))]} icon={LayoutGrid} className="w-full" placeholder="All Categories"/>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                  <ThemeSelect value={tempFilters.paymentMode} onChange={(val) => setTempFilters({ ...tempFilters, paymentMode: val })} options={[{ label: 'All Modes', value: '' }, { label: 'COD', value: 'COD' }, { label: 'PREPAID', value: 'PREPAID' }]} icon={CreditCard} className="w-full" placeholder="All Modes"/>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign {filterOptionsLoading && <LoadingDot />}</label>
                  <ThemeSelect value={tempFilters.campaignName} onChange={(val) => setTempFilters({ ...tempFilters, campaignName: val })} options={[{ label: 'All Campaigns', value: '' }, ...filterOptions.campaigns.map(c => ({ label: c, value: c }))]} icon={Megaphone} className="w-full" placeholder="All Campaigns"/>
                </div>
              </div>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50">
              <button onClick={handleResetFilters} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Reset</button>
              <button onClick={handleApplyFilters} className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: currentTheme.color }}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Dispatch Button */}
      {selectedOrders.length > 0 && has('DISPATCH_EDIT') && (
        <div className="fixed bottom-8 right-8 animate-bounce">
          <button onClick={handleOpenBulkDispatchModal}
            className="flex items-center space-x-2 px-6 py-3 desktop:px-8 desktop:py-4 text-white rounded-lg shadow-lg transition-all desktop:text-lg"
            style={{ backgroundColor: currentTheme.color }}>
            <Truck className="w-5 h-5 desktop:w-6 desktop:h-6"/>
            <span>Dispatch ({selectedOrders.length})</span>
          </button>
        </div>
      )}

      {/* Dispatch Alert */}
      {showDispatchAlert && (
        <div className="fixed top-20 right-10 z-[9999] pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[380px] overflow-hidden animate-slide-in-right pointer-events-auto">
            <div className="flex justify-between items-center px-4 py-2 border-b border-gray-100 bg-gray-50/30">
              <h3 className="text-sm font-semibold text-gray-800">Alert</h3>
              <button onClick={() => setShowDispatchAlert(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-4 h-4 text-gray-500"/>
              </button>
            </div>
            <div className="p-4 flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-orange-500"/>
                </div>
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-md">
                  <div className="w-4 h-4 bg-orange-200 rounded-full flex items-center justify-center">
                    <Clock className="w-2.5 h-2.5 text-orange-600"/>
                  </div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-medium text-red-600 mb-2 leading-normal animate-pulse">
                  {totalOrders} orders pending for dispatch
                </h2>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default DispatchOrderverified;